import { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { ReportingResultModel, getReportsInput } from 'api-hooks/report';
import { ApiError } from 'common/repositories/common.model';
import LoadingComponent from 'components/common/loading';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import ListHeader from 'components/elements/list-header';
import { PivotTable } from 'components/pivot';
import { PivotTableProvider } from 'components/pivot/hook';
import { useApplyQueryPivot } from 'components/pivot/pivot-setting';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useComposedQuery from 'hooks/use-composed-query';
import useSetDefaultFilters from 'hooks/use-set-default-filters';
import { isEmpty } from 'lodash';
import * as React from 'react';

import ActionComponent from './action-component';
import useExtractCompleteCompactFilter from '../hooks/use-extract-complete-compact-filter';

interface PivotReportTemplateProps {
  completeName?: string[];
  usePivotQuery: (
    input?: getReportsInput,
    options?: UseQueryOptions<ReportingResultModel, ApiError>,
  ) => UseQueryResult<ReportingResultModel, ApiError>;
  title: string;
  defaultFilters?: {
    name: string;
    value: string | undefined;
  }[];
  isShowAllFilterCompleted?: boolean;
}

export default function PivotReportTemplate(props: PivotReportTemplateProps) {
  const {
    completeName = [],
    usePivotQuery,
    title,
    defaultFilters = [],
    isShowAllFilterCompleted,
  } = props;

  const {
    data,
    refetch,
    isLoading,
    error,
    isError,
    query,
    extras: [{ filters = [], setFilters }, { pivot, setPivot }],
  } = useComposedQuery(
    usePivotQuery,
    {},
    useApplyQueryFilter((data: any) => data.filters),
    useApplyQueryPivot((data: any) => data.pivot),
  );

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error?.message });
    }
  }, [error]);

  const { filterComponent } = useExtractCompleteCompactFilter({
    filters,
    completeName,
    isShowAllFilterCompleted,
  });

  useSetDefaultFilters({
    filters,
    setFilters,
    values: defaultFilters,
  });

  const filter =
    filters &&
    filters.reduce((prev, next) => {
      if (next.value !== undefined && next.value !== null) {
        return {
          ...prev,
          [next.name]: next.value,
        };
      }
      return prev;
    }, {});

  const rows: undefined | string = React.useMemo(() => {
    let filterable = false;
    const _rows = (pivot?.value?.rows || []).map((r, i) => {
      if (
        r.name !== pivot?.default.rows[i].name ||
        r.showTotal !== pivot?.default.rows[i].showTotal
      ) {
        filterable = true;
      }

      return {
        name: r.name,
        show_total: !!r.showTotal,
      };
    });

    if (filterable) {
      return _rows as any as string;
    }
  }, [pivot]);

  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
      }}
    >
      <PivotTableProvider filter={filter} pivot={rows}>
        {(ExportButton) => (
          <>
            <div>
              <ListHeader
                query={query}
                showAdd={false}
                showRefresh={false}
                title={title}
                rightCustomComponent={ActionComponent({
                  exportComponent: <ExportButton />,
                  onRefresh: refetch,
                  pivotSetting: {
                    isEditable: !isEmpty(pivot) && pivot?.isEditable,
                    setPivot,
                    pivot,
                  },
                  isLoading,
                  isError,
                })}
              />
              {filterComponent}
              <Separator gap={16} />
            </div>
            <>
              {isLoading ? (
                <LoadingComponent />
              ) : (
                <>
                  <PivotTable queryResultData={data} />
                </>
              )}
            </>
          </>
        )}
      </PivotTableProvider>
    </FilterHandleParentProvider>
  );
}
